/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  EmptyState,
  EmptyStateProps,
  ICON_CLOUD_QUEUE,
  ICON_LINK,
  ICON_LIST_WITH_HELP_OUTLINE,
  SPINNER_TYPE_REGULAR,
  SdsIcon,
  Spinner,
} from '@squiz/sds';
import { EnvironmentBadge } from 'apps/administration/src/Components/StatusBadge/EnvironmentBadge/EnvironmentBadge';
import { InstanceStatusBadge } from 'apps/administration/src/Components/StatusBadge/InstanceStatusBadge/InstanceStatusBadge';
import { DXP_CONSOLE_VALUE_NOT_SET } from 'apps/console-core/src/Constants/App.constants';
import { useRegion } from 'apps/console-core/src/Hooks/UseRegion/UseRegion.hook';
import {
  CONSOLE_INSTANCE_BASE_HOSTING_TYPE_DEDICATED,
  CONSOLE_INSTANCE_LIST_PURPOSE_PRODUCT_PAGE,
  ConsoleInstanceStatusCode,
  ConsoleInstanceType,
  PRODUCT_INSTANCE_STATUS_ACTIVE,
  PRODUCT_INSTANCE_STATUS_UNAVAILABLE,
  useInstances,
} from 'apps/console-core/src/Services/console';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { INSTANCE_MANAGEMENT_STRINGS } from '../../Constants/Strings.constants';
import { PRODUCT_INSTANCE_LIST_DATA_IDS } from './ProductInstanceList.constants';
import { ProductInstanceListItem } from './ProductInstanceList.defs';
import { ProductInstanceListProps } from './ProductInstanceList.props';
import { convertForInstanceList } from './utils/ConvertForInstanceList';

/**
 * Displays a list of product instances.
 */
export const ProductInstanceList = (props: ProductInstanceListProps): JSX.Element => {
  const { t } = useTranslation();
  const regionMap = useRegion();

  const { instanceType, productKey } = props;

  // Labels
  const labels = useMemo(() => {
    return {
      dedicated: t(INSTANCE_MANAGEMENT_STRINGS.dedicated),
      hosted: t(INSTANCE_MANAGEMENT_STRINGS.hostedIn),
      noInstances: t(INSTANCE_MANAGEMENT_STRINGS.noInstances),
    };
  }, [t]);

  // Get instance array
  const instances = useInstances(
    instanceType,
    ConsoleInstanceStatusCode.Live,
    CONSOLE_INSTANCE_LIST_PURPOSE_PRODUCT_PAGE,
  );

  // Loading state
  if (!instances) {
    return (
      <div className="loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  const listItems = convertForInstanceList(instances, productKey);
  const showRegion = instanceType !== ConsoleInstanceType.connect;

  if (listItems.length === 0) {
    const emptyStateProps: EmptyStateProps = {
      caption: labels.noInstances,
      classes: 'product-instance-list__empty-state',
      svg: ICON_LIST_WITH_HELP_OUTLINE,
    };

    return <EmptyState {...emptyStateProps} />;
  }

  const list = listItems.map((item: ProductInstanceListItem) => {
    const { name, instanceId, region, environment, hostingType, url } = item;
    const isClickable = url !== DXP_CONSOLE_VALUE_NOT_SET;

    const itemClass = clsx(`organisation-product-instance__item`, `instance--${instanceType}`, {
      [`organisation-product-instance__link`]: isClickable,
    });

    // If 'displayUrl' is not defined, default to directly rendering the 'url'.
    const { displayUrl = url } = item;

    const regionName = regionMap[region];
    const instanceStatus = isClickable ? PRODUCT_INSTANCE_STATUS_ACTIVE : PRODUCT_INSTANCE_STATUS_UNAVAILABLE;

    /*
    | Matrix
    +----------+-------------+-------------+
    | isLinked | hostingType | Label       |
    +----------+-------------+-------------+
    | true     | dedicated   | Dedicated   |
    | false    | dedicated   | Dedicated   |
    | false    | saas        | SaaS        |
    +----------+-------------+-------------+
    */

    /*
    | Funnelback
    +----------+----------------+-------------+
    | isLinked | hostingType    | Label       |
    +----------+----------------+-------------+
    | true     | multi-tenanted | SaaS        |
    | true     | dedicated      | Dedicated   |
    | false    | saas           | SaaS        |
    +----------+----------------+-------------+
    */

    let hostText = (
      <abbr title={INSTANCE_MANAGEMENT_STRINGS.saasDescription}>{INSTANCE_MANAGEMENT_STRINGS.saasAbbr}</abbr>
    );

    if (hostingType === CONSOLE_INSTANCE_BASE_HOSTING_TYPE_DEDICATED) {
      hostText = <>{labels.dedicated}</>;
    }

    const environmentBadge = environment ? <EnvironmentBadge environment={environment} /> : null;

    const listItemContent = (
      <>
        <div className="organisation-product-instance__body">
          <div className="organisation-product-instance__info">
            <span className="organisation-product-instance__name">{name}</span>
            {environmentBadge}
            {isClickable && url && (
              <span className="organisation-product-instance__url">
                <SdsIcon svg={ICON_LINK} /> <span>{displayUrl}</span>
              </span>
            )}
          </div>
          {showRegion && (
            <div className="organisation-product-instance__region">
              <SdsIcon svg={ICON_CLOUD_QUEUE} />
              <span className="organisation-product-instance__hosting">
                <span className="organisation-product-instance__text--strong">{hostText}</span>
                {regionName && (
                  <>
                    <span>, {labels.hosted}&nbsp;</span>
                    <span className="organisation-product-instance__text--strong">{regionName}</span>
                  </>
                )}
              </span>
            </div>
          )}
          <div className="organisation-product-instance__status">
            <InstanceStatusBadge status={instanceStatus} />
          </div>
        </div>
      </>
    );

    return (
      <li className={itemClass} key={instanceId}>
        {isClickable ? (
          <button
            data-id={PRODUCT_INSTANCE_LIST_DATA_IDS.productInstanceLink}
            data-url={url}
            onClick={(): void => {
              window.open(url, '_blank', 'noopener');
            }}
          >
            {listItemContent}
          </button>
        ) : (
          <div>{listItemContent}</div>
        )}
      </li>
    );
  });

  return <ul className="organisation-product-instance__list">{list}</ul>;
};
